import { XMarkIcon } from "@heroicons/react/24/outline";
import { useContext, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { Modal } from "src/components/DialogComponent";
import TGButton from "src/components/ui/TGButton";
import RejectedPassingTest from "src/components/ui-icons/tests/RejectedPassingTest";
import SuccessfullyPassingTest from "src/components/ui-icons/tests/SuccessfullyPassingTest";
import { twMerge } from "tailwind-merge";
import { IAnswer, IAnswersResult, IQuestion, QUESTION_TYPES, TEST_STATUSES } from "../Tests/types";
import { PassingTestContext } from "./passingTestContext";

export default function PassingTestMobile({ zIndex = 90 }: { zIndex?: number }) {
  const [countCorrectAnswers, setCountCorrectAnswers] = useState(0);
  const { questions, getAnswersResult } = useContext(PassingTestContext);
  const methods = useForm<IQuestion>();
  const { handleSubmit } = methods;

  const checkCorrectAnswers = (data: any) => {
    return questions?.map((question: IQuestion) => {
      return {
        id: question.ID,
        isRight: question.Answers.every(
          (answer) => !!answer.IsCorrect === !!data[answer.ID],
        ),
      };
    });
  };

  const onSubmit: SubmitHandler<IQuestion> = (data: any) => {
    const testResults = checkCorrectAnswers(data);
    getAnswersResult(data);
    setCountCorrectAnswers(
      testResults.filter(
        (question: { id: string; isRight: boolean }) => question.isRight === true,
      ).length,
    );
  };

  return (
    <div className="flex min-h-full flex-col">
      <PassingTestHeaderMobile />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 grow flex-col">
          <div className="grow px-5 py-5">
            <div className="space-y-3">
              {questions?.map((question: IQuestion) => (
                <QuestionCardTestMobile key={question.ID} question={question} />
              ))}
            </div>
          </div>
          <PassingTestFooterMobile
            countCorrectAnswers={countCorrectAnswers}
            zIndex={zIndex}
          />
        </form>
      </FormProvider>
    </div>
  );
}

function PassingTestHeaderMobile() {
  const { answersResult, showResultsTest, percentResultTest, startPassingTest } =
    useContext(PassingTestContext);

  return (
    <header className="flex items-start justify-between border-b px-5 pb-4 pt-5">
      <div className="min-w-0">
        <h1 className="text-[18px] font-medium leading-[28px] text-[#111827]">
          Тестирование
        </h1>
        {!!Object.keys(answersResult).length && showResultsTest ? (
          <span className="mt-1 block text-[14px] font-medium leading-[20px] text-[#22AD5C]">
            Результат: {percentResultTest}%
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={startPassingTest}
        className="shrink-0 text-[#111827]"
      >
        <XMarkIcon className="h-8 w-8" />
      </button>
    </header>
  );
}

function PassingTestFooterMobile({
  countCorrectAnswers,
  zIndex,
}: {
  countCorrectAnswers: number;
  zIndex: number;
}) {
  const { showResultsTest, startPassingTest } = useContext(PassingTestContext);

  return (
    <footer className="border-t p-5">
      <div className="flex flex-col gap-3">
        {!showResultsTest && (
          <ModalResultPassingTestMobile
            countRightAnswers={countCorrectAnswers}
            zIndex={zIndex + 10}
          />
        )}
        <TGButton
          type="button"
          color="white"
          onClick={startPassingTest}
          className="h-[44px] w-full justify-center text-[14px]"
        >
          Отмена
        </TGButton>
      </div>
    </footer>
  );
}

function QuestionCardTestMobile({ question }: { question: IQuestion }) {
  const { register, setValue, watch } = useFormContext();
  const { showResultsTest, answersResult } = useContext(PassingTestContext);
  const isQuestionTypeMultiple = question.Type === QUESTION_TYPES.MULTIPLE;
  const checkCorrectQuestion = question.Answers.every(
    (answer) => !!answer.IsCorrect === !!answersResult[answer.ID],
  );
  const resultQuestion =
    !!Object.keys(answersResult).length && showResultsTest ? (
      checkCorrectQuestion ? (
        <SuccessfullyPassingTest />
      ) : (
        <RejectedPassingTest />
      )
    ) : null;

  const handleChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.name, e.target.checked);
  };

  const handleChangeRadio = (answerId: string) => {
    question.Answers.forEach((answer) => setValue(answer.ID.toString(), null));
    setValue(answerId, "on");
  };

  return (
    <div className="rounded-lg border border-[#DFE4EA] bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <h2 className="text-[16px] font-medium leading-[24px] text-[#111827]">
          {question.Description}
        </h2>
        <span className="shrink-0">{resultQuestion}</span>
      </div>

      <p className="mb-4 text-[14px] leading-[20px] text-[#637381]">
        {isQuestionTypeMultiple
          ? "Можно выбрать несколько вариантов ответа"
          : "Выберите 1 вариант ответа"}
      </p>

      <div className="space-y-3">
        {question.Answers?.map((answer: IAnswer) => {
          const checked =
            !!Object.keys(answersResult).length && showResultsTest
              ? answersResult[answer.ID]
              : watch(answer.ID.toString());

          return (
            <label
              key={answer.ID}
              className={twMerge(
                checked ? "border-darkblue bg-[#F8FAFF]" : "border-[#DFE4EA] bg-white",
                "flex w-full items-start gap-3 rounded-lg border p-3",
                !!Object.keys(answersResult).length &&
                  showResultsTest &&
                  "cursor-default",
              )}
            >
              <input
                disabled={!!Object.keys(answersResult).length && showResultsTest}
                {...register(answer.ID.toString())}
                type={isQuestionTypeMultiple ? "checkbox" : "radio"}
                className="sr-only"
                onChange={(e) =>
                  isQuestionTypeMultiple
                    ? handleChangeCheckBox(e)
                    : handleChangeRadio(answer.ID.toString())
                }
                checked={!!checked}
              />
              <AnswerIndicator
                checked={!!checked}
                multiple={isQuestionTypeMultiple}
              />
              <span className="min-w-0 break-words text-[14px] leading-[20px] text-[#111827]">
                {answer.Description}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function AnswerIndicator({
  checked,
  multiple,
}: {
  checked: boolean;
  multiple: boolean;
}) {
  if (multiple) {
    return (
      <span
        className={twMerge(
          checked ? "border-darkblue" : "border-[#DFE4EA]",
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-white",
        )}
      >
        <span className={twMerge(checked ? "opacity-100" : "opacity-0")}>
          <svg
            width="11"
            height="8"
            viewBox="0 0 11 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
              fill="#012169"
              stroke="#012169"
              strokeWidth="0.4"
            />
          </svg>
        </span>
      </span>
    );
  }

  return (
    <span
      className={twMerge(
        checked ? "border-darkblue" : "border-[#DFE4EA]",
        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-white",
      )}
    >
      <span
        className={twMerge(
          checked ? "bg-darkblue opacity-100" : "opacity-0",
          "h-2.5 w-2.5 rounded-full",
        )}
      />
    </span>
  );
}

function ModalResultPassingTestMobile({
  countRightAnswers,
  zIndex,
}: {
  countRightAnswers: number;
  zIndex: number;
}) {
  const { setValue } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);
  const {
    questions,
    minCorrectAnswerCount,
    answersResult,
    startPassingTest,
    onResult,
    getPercentResultTest,
  } = useContext(PassingTestContext);

  const successfulPassingTest = +countRightAnswers >= +minCorrectAnswerCount;

  const percentageCorrectAnswers = useMemo(() => {
    if (!questions.length) {
      return 0;
    }

    const correctQuestions = questions.map((question: IQuestion) =>
      question.Answers.every(
        (answer) => !!answer.IsCorrect === !!answersResult[answer.ID],
      ),
    );

    return (
      (correctQuestions.filter((correctQuestion) => correctQuestion).length /
        questions.length) *
      100
    );
  }, [answersResult, questions]);

  const backButton = (result: TEST_STATUSES) => {
    getPercentResultTest(Math.trunc(percentageCorrectAnswers));
    onResult(result);
    setOpenDialog(false);
    startPassingTest();
  };

  const restartTest = () => {
    setOpenDialog(false);

    questions?.forEach((question: IQuestion) => {
      question?.Answers?.forEach((answer: IAnswer) => {
        question.Type === QUESTION_TYPES.MULTIPLE
          ? setValue(answer.ID.toString(), false)
          : setValue(answer.ID.toString(), null);
      });
    });
  };

  return (
    <>
      <TGButton
        type="submit"
        onClick={() => setOpenDialog(true)}
        color="bluedark"
        className="h-[44px] w-full justify-center text-[14px]"
      >
        Проверить
      </TGButton>

      <Modal
        zIndex={zIndex}
        open={openDialog}
        modalClassName="!min-h-0 !w-[calc(100vw-24px)] !min-w-0 rounded-xl py-5"
        toggleDialog={setOpenDialog}
      >
        <header className="px-5 pb-2">
          <h2>
            {successfulPassingTest
              ? "Тест пройден успешно"
              : "Тест не пройден"}
          </h2>
        </header>

        <div className="grow px-5 py-4">
          <h4 className="text-black">
            Вы ответили правильно на {countRightAnswers} из {questions.length}{" "}
            вопросов.
          </h4>
          {!successfulPassingTest ? (
            <h4 className="mt-2 text-black">
              Пожалуйста, пройдите тест повторно.
            </h4>
          ) : null}
        </div>

        <footer className="flex flex-col gap-3 border-t px-5 pt-4">
          {successfulPassingTest ? (
            <TGButton
              color="bluedark"
              onClick={() => backButton(TEST_STATUSES.COMPLETE_SUCCESSFUL)}
              className="h-[44px] w-full justify-center text-[14px]"
            >
              Закрыть
            </TGButton>
          ) : (
            <>
              <TGButton
                color="bluedark"
                onClick={restartTest}
                className="h-[44px] w-full justify-center text-[14px]"
              >
                Перейти к тесту
              </TGButton>
              <TGButton
                onClick={() => backButton(TEST_STATUSES.COMPLETE_WITH_ERROR)}
                className="h-[44px] w-full justify-center text-[14px]"
              >
                Закрыть тест
              </TGButton>
            </>
          )}
        </footer>
      </Modal>
    </>
  );
}

export function getPassingTestStatusLabel(
  answersResult: IAnswersResult,
  showResultsTest: boolean,
  percentResultTest: number,
) {
  if (!Object.keys(answersResult).length || !showResultsTest) {
    return null;
  }

  return `Результат: ${percentResultTest}%`;
}
